import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { UseFormSetError, UseFormSetValue } from 'react-hook-form';
import {
  PermissionEnum,
  GeneralLedgerTransaction as GeneralLedgerTransactionType,
  AccountType,
  Account,
  Response,
  ResponseError,
  responseErrorsResolver,
  TransactionTypeEnum,
  SelectItemProps,
  DocumentStatus,
  DocumentType,
  Document,
  Currency,
} from '@ui/src';
import { useAppDispatch } from '@store';
import {
  useAddDocumentToGLTransactionMutation,
  useCreateGLTransactionMutation,
  useDeleteGLDocumentsMutation,
  useDeleteGLTransactionMutation,
  useGetGLTransactionValidatorsQuery,
  useGetTransactionsAccountsCategoriesQuery,
  useLazyGetAllCreditAccountBalancesQuery,
  useLazyGetAllDebitAccountBalancesQuery,
  useLazyGetGeneralLedgerListQuery,
  useLazyGetGeneralTransactionByIdQuery,
  useLazyGetGLDocumentQuery,
  useLazyGetGLDocumentsQuery,
  useLazyGetTransactionsAccountBalanceByIdQuery,
  useLazyGetTransactionsAccountsListQuery,
} from '../store/services';
import { useCheckDocumentStringNameMutation } from '../../../../../../store/service';
import {
  useAddCommentMutation,
  useChangeDocumentCategoryMutation,
  useChangeDocumentStatusMutation,
  useDeleteCommentMutation,
  useEditCommentMutation,
  useLazyGetTransactionChangeLogsQuery,
  useLazyGetTransactionDocumentFileQuery,
  useTransactionsUploadMutation,
} from '../../transactions/store/services';
import useRetry from '../../outgoing/helpers/use-retry';
import { UploadResponse } from '../../transactions/type';
import { Balance } from '../../../types/account';
import { clearGeneralLedgerTransactionState } from '../store/slices/transaction';

import ROUTES from '../../../routes';

const useRequests = ({
  transactionId,
  transaction,
  transactionsTypes,
  search,
  documents,
  creditAccountNumber,
  debitAccountNumber,
  document: documentItem,
  setError,
  currencyId,
  baseCurrency,
  panelIsOpen,
  prevPanelIsOpen,
  selectedTake,
  restatement,
  setValue,
  debitBalances,
  creditBalances,
  currencies,
  isDirty,
  fields,
  activePermissions,
}: {
  transactionId?: string;
  transaction?: GeneralLedgerTransactionType;
  transactionsTypes?: { id: string; name: string }[];
  search?: string;
  documents?: Document[];
  creditAccountNumber?: string;
  debitAccountNumber?: string;
  document?: Document;
  setError: UseFormSetError<any>;
  currencyId?: string;
  baseCurrency?: Currency;
  panelIsOpen: boolean;
  prevPanelIsOpen?: boolean;
  selectedTake?: number;
  restatement?: boolean;
  setValue: UseFormSetValue<any>;
  debitBalances?: Balance[];
  creditBalances?: Balance[];
  currencies?: SelectItemProps[];
  isDirty?: boolean;
  fields?: any;
  activePermissions?: PermissionEnum[];
}) => {
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [debitAccounts, setDebitAccounts] = useState<Account[]>([]);
  const [creditAccounts, setCreditAccounts] = useState<Account[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [availableCurrencies, setAvailableCurrencies] = useState<
    SelectItemProps[]
  >([]);
  const [attachedDocumentsErrors, setAttachedDocumentsErrors] = useState<
    | Record<
        string,
        {
          name: string;
          error: string;
        }
      >
    | undefined
  >(undefined);
  const [uploadedDocumentsErrors, setUploadedDocumentsErrors] = useState<
    { name: string; error: string }[]
  >([]);
  const [uploadedFilesError, setUploadedFilesError] = useState<string[]>([]);
  const [imgLink, setImgLink] = useState('');
  const [dateWithTime, setDateWithTime] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useGetGLTransactionValidatorsQuery();

  const [
    getTransactionDocuments,
    { isLoading: documentsIsLoading, isFetching: documentsIsFetching },
  ] = useLazyGetGLDocumentsQuery();

  const [create, { isLoading: createIsLoading, isSuccess: createIsSuccess }] =
    useCreateGLTransactionMutation();

  const [getTransactionsList] = useLazyGetGeneralLedgerListQuery();
  const [
    getDebitAccountBalanceById,
    { isLoading: debitBalanceIsLoading, isFetching: debitBalanceIsFetching },
  ] = useLazyGetTransactionsAccountBalanceByIdQuery();
  const [
    getCreditAccountBalanceById,
    { isLoading: creditBalanceIsLoading, isFetching: creditBalanceIsFetching },
  ] = useLazyGetTransactionsAccountBalanceByIdQuery();
  const [
    getTransactionChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetTransactionChangeLogsQuery();
  const [checkDocumentName, { isLoading: checkDocumentNameInProgress }] =
    useCheckDocumentStringNameMutation();
  const [upload, { isLoading: fileUploadInProgress }] =
    useTransactionsUploadMutation();
  const [
    getTransactionDocumentFile,
    {
      isLoading: downloadInProgress,
      isFetching: downloadIsFetching,
      isSuccess: downloadFileIsSuccess,
    },
  ] = useLazyGetTransactionDocumentFileQuery();
  const [addComment, { isLoading: commentInProgress }] =
    useAddCommentMutation();
  const { isLoading: accountsCategoriesLoading } =
    useGetTransactionsAccountsCategoriesQuery(
      'all=true&include=primaryAccounts',
    );
  const [
    getTransactionDocumentyId,
    { isLoading: documentIsLoading, isFetching: documentIsFetching },
  ] = useLazyGetGLDocumentQuery();

  const [
    getDebitAccountsList,
    {
      isLoading: debitAccountsListLoading,
      isFetching: debitAccountsListUpdateInProgress,
    },
  ] = useLazyGetTransactionsAccountsListQuery();

  const [
    getAllDebitAccountBalances,
    {
      isLoading: allDebitAccountBalancesIsLoading,
      isFetching: allDebitAccountBalancesisFetching,
    },
  ] = useLazyGetAllDebitAccountBalancesQuery();

  const [
    getAllCreditAccountBalances,
    {
      isLoading: allCreditAccountBalancesIsLoading,
      isFetching: allCreditAccountBalancesisFetching,
    },
  ] = useLazyGetAllCreditAccountBalancesQuery();
  const [
    addDocumentsToTransaction,
    { isLoading: addDocumentInProgress, isSuccess: fileUploadIsSuccess },
  ] = useAddDocumentToGLTransactionMutation();
  const [changeDocumentStatus, { isLoading: documentStatusChangeInProgress }] =
    useChangeDocumentStatusMutation();
  const [
    changeDocumentCategory,
    { isLoading: documentCategoryChangeInProgress },
  ] = useChangeDocumentCategoryMutation();
  const [
    editComment,
    { isLoading: editCommentInProgress, isSuccess: editCommentIsSuccess },
  ] = useEditCommentMutation();
  const [deleteComment, { isLoading: deleteCommentInProgress }] =
    useDeleteCommentMutation();
  const [
    deleteDocument,
    { isLoading: deleteDocumentInProgress, isSuccess: deleteDocumentIsSuccess },
  ] = useDeleteGLDocumentsMutation();
  const [
    getCreditAccountsList,
    {
      isLoading: creditAccountsListLoading,
      isFetching: creditAccountsListUpdateInProgress,
    },
  ] = useLazyGetTransactionsAccountsListQuery();
  const [
    deleteTransaction,
    { isSuccess: deleteIsSuccess, isLoading: deleteIsLoading },
  ] = useDeleteGLTransactionMutation();

  const [getGeneralTransactionById, { isLoading }] =
    useLazyGetGeneralTransactionByIdQuery();

  const { getTransaction, onClearTimer, internalError } = useRetry({
    getTransactionFunction: getGeneralTransactionById,
    id: transactionId as string,
    isRetry: true,
  });

  const getTransationDocumentsAction = (cursor?: string) => {
    const body: {
      id: string;
      cursor?: string;
    } = {
      id: transactionId as string,
    };

    if (cursor) {
      body.cursor = cursor;
    }

    getTransactionDocuments(body);
  };

  const linkedTransactionPath = useMemo(() => {
    const currentTransactionType = transactionsTypes?.find(
      (item) => item.id === transaction?.transaction?.typeId,
    );

    if (currentTransactionType) {
      let link = '#';

      if (currentTransactionType?.name === TransactionTypeEnum.INTERNAL) {
        link = ROUTES.INTERNAL_TRANSACTION_READ;
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.INCOMING
      ) {
        link = ROUTES.INCOMING_TRANSACTION;
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.OUTGOING
      ) {
        link = ROUTES.OUTGOING_TRANSACTION;
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.CURRENCY_EXCHANGE
      ) {
        link = ROUTES.EXCHANGE_READ;
      } else if (currentTransactionType?.name === TransactionTypeEnum.NOSTRO) {
        link = ROUTES.NOSTRO_TRANSFER;
      } else if (currentTransactionType?.name === 'FEE') {
        link = ROUTES.GENERAL_LEDGER_FEE;
      }

      return `${generatePath(link, {
        transactionId: transaction?.parentTransactionId,
      })}${search || ''}`;
    }

    return undefined;
  }, [
    transactionsTypes,
    transaction?.transaction?.typeId,
    transaction?.parentTransactionId,
  ]);

  const onDeleteDocument = (id: string) => {
    deleteDocument({
      id: transactionId as string,
      body: {
        ids: [id],
      },
    });
  };

  const onMoveDocument = (data: { ids: string[]; category: DocumentType }) => {
    changeDocumentCategory({
      id: transactionId as string,
      body: data,
    });
  };

  const onChangeCustomerDocumentsStatus = (data: {
    ids: string[];
    status: DocumentStatus;
  }) => {
    changeDocumentStatus({
      id: transactionId as string,
      body: data,
    });
  };

  const getAllDebitAccountBalancesAction = useCallback((id: string) => {
    getAllDebitAccountBalances({
      id,
    });
  }, []);

  const getAllCreditAccountBalancesAction = useCallback((id: string) => {
    getAllCreditAccountBalances({
      id,
    });
  }, []);

  const onDownloadDocument = async (id: string) => {
    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    const currentDocument = documents?.find((item) => item.id === id);

    if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${currentDocument?.name}`;
      hiddenElement.click();
    }
  };

  const getTransactionChangeLogsAction = useCallback(
    (cursor?: string) => {
      getTransactionChangeLogs(
        `?take=20&entityType=GENERAL_LEDGER_TRANSACTION&entityId=${transactionId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [transactionId],
  );

  const getDebitAccountsListAction = useCallback(async (query: string) => {
    if (query && query.length) {
      const response = await getDebitAccountsList(
        `?${query}&all=true&sortProp=fullNumber&sortDir=asc${
          transactionId ? '' : '&closed=false'
        }`,
      );

      if (response && response.data) {
        const filteredRes = response.data.data.edges
          .map((item) => item.node)
          .filter((item) => item.fullNumber !== creditAccountNumber);

        setDebitAccounts(filteredRes);
      }
    }
  }, []);

  const getCreditAccountsListAction = useCallback(async (query: string) => {
    if (query && query.length) {
      const response = await getCreditAccountsList(
        `?${query}&all=true&sortProp=fullNumber&sortDir=asc${
          transactionId ? '' : '&closed=false'
        }`,
      );

      if (response && response.data) {
        const filteredRes = response.data.data.edges
          .map((item) => item.node)
          .filter((item) => item.fullNumber !== debitAccountNumber);

        setCreditAccounts(filteredRes);
      }
    }
  }, []);

  const onSendDocumentComment = (text: string) => {
    addComment({
      id: documentItem?.id as string,
      body: {
        text,
      },
    });
  };

  const onEditComment = (data: {
    id: string;
    commentId: string;
    body: {
      text: string;
    };
  }) => {
    editComment({
      id: data.id,
      commentId: data.commentId,
      body: data.body,
    });
  };

  const onDeleteComment = (data: { id: string; commentId: string }) => {
    deleteComment({
      id: data.id,
      commentId: data.commentId,
    });
  };

  const onUploadDocument = async (files: FileList, organizationId?: string) => {
    setAttachedDocumentsErrors(undefined);
    setUploadedDocumentsErrors([]);

    if (setUploadedFilesError) {
      setUploadedFilesError([]);
    }
    const nextUploadedFilesErrors: string[] = [];
    const nextUploadedDocumentsErrors: { name: string; error: string }[] = [];

    const filesResponse = await Promise.all(
      Object.keys(files).map(async (file) => {
        const fileItem = files[file as any];

        const checkNameResponse = (await checkDocumentName({
          value: fileItem.name,
        })) as {
          error?: {
            data: {
              message?: {
                constraints: Record<string, string>;
              }[];
            };
          };
        };

        let nextFile;

        if (!checkNameResponse?.error) {
          const response = (await upload(files[file as any])) as unknown as {
            data?: UploadResponse[];
            error?: {
              originalStatus: number;
              data?: {
                message?: string;
              };
            };
            message?: string;
          };

          if (response && response.data) {
            nextFile = { ...response.data[0] };

            let fileName = fileItem?.name;

            if (
              response.data[0] &&
              response.data[0]?.mimeType?.includes('image')
            ) {
              fileName = `${fileItem?.name.slice(0, fileItem?.name.lastIndexOf('.'))}.jpg`;
            }

            nextFile.name = fileName;
          } else if (response.error) {
            if (response.error.originalStatus === 413) {
              nextUploadedFilesErrors.push(
                `${files[file as any].name} size too large`,
              );
            } else {
              // eslint-disable-next-line no-lonely-if
              if (response?.error?.data?.message) {
                nextUploadedFilesErrors.push(response?.error?.data?.message);
              }
            }
          }
        } else if (
          checkNameResponse?.error &&
          checkNameResponse?.error?.data?.message
        ) {
          const errorItem =
            checkNameResponse?.error?.data?.message[0]?.constraints;

          if (errorItem) {
            nextUploadedDocumentsErrors.push({
              name: files[file as any].name,
              error: Object.values(errorItem)[0],
            });
          }
        }

        return nextFile;
      }),
    );

    setUploadedDocumentsErrors(nextUploadedDocumentsErrors);

    if (setUploadedFilesError) {
      setUploadedFilesError(nextUploadedFilesErrors);
    }

    if (filesResponse && filesResponse.length) {
      const nextAttachedDocumentsErrors = { ...attachedDocumentsErrors };

      const uploadedFiles: { name: string; uploadId: string }[] = [];

      filesResponse.forEach((item) => {
        if (item && item.name && item.id) {
          uploadedFiles.push({
            name: item?.name,
            uploadId: item?.id,
          });
        }
      });

      if (uploadedFiles.length) {
        await Promise.all(
          uploadedFiles.map(async (item) => {
            const body: {
              uploadId: string;
              name: string;
              organizationId?: string;
            } = {
              uploadId: item.uploadId,
              name: item.name,
            };

            if (organizationId) {
              body.organizationId = organizationId;
            }

            const response = (await addDocumentsToTransaction({
              id: transactionId as string,
              body,
            })) as {
              error?: {
                data: {
                  message?: {
                    constraints: Record<string, string>;
                  }[];
                };
              };
            };

            if (response.error && response?.error?.data?.message?.length) {
              const errorItem = response?.error?.data?.message[0].constraints;

              if (errorItem) {
                nextAttachedDocumentsErrors[item.uploadId] = {
                  name: item.name,
                  error: Object.values(errorItem)[0],
                };
              }
            }

            return response;
          }),
        );

        setAttachedDocumentsErrors(nextAttachedDocumentsErrors);
      }
    }
  };

  const getTransactionDocumentByIdAction = async (id: string) => {
    getTransactionDocumentyId({
      id: transactionId as string,
      documentId: id,
    });

    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    if (response && response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setImgLink(blob);
    }
  };

  const handleDelete = async () => {
    setDeleteError('');

    const response = (await deleteTransaction(transactionId as string)) as {
      error: ResponseError | { data: { message: string } };
    };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error as ResponseError,
        setError,
        setGlobalError: setDeleteError,
      });
    }
  };

  const onSearch = async (name: string, searchText: string) => {
    const results: Account[] = [];

    if (searchText) {
      if (name.toLowerCase().indexOf('debit') >= 0) {
        const response = (await getDebitAccountsList(
          `?search=${searchText}&all=true${
            transactionId ? '' : '&closed=false'
          }`,
        )) as { data: Response<Account> };

        if (response?.data?.data) {
          response.data.data.edges?.forEach((item) => {
            results.push(item.node);
          });
        }

        const filteredRes = results.filter(
          (item) => item.fullNumber !== creditAccountNumber,
        );

        setDebitAccounts(filteredRes);
      } else if (name.toLowerCase().indexOf('credit') >= 0) {
        const response = (await getCreditAccountsList(
          `?search=${searchText}&all=true${
            transactionId ? '' : '&closed=false'
          }`,
        )) as { data: Response<Account> };

        if (response?.data?.data) {
          response.data.data.edges?.forEach((item) => {
            results.push(item.node);
          });
        }

        const filteredRes = results.filter(
          (item) => item.fullNumber !== debitAccountNumber,
        );

        setCreditAccounts(filteredRes);
      }
    }
  };

  const onUpdateBalance = (
    type: AccountType.DEBIT | AccountType.CREDIT,
    accountId: string,
  ) => {
    let query = '';

    if (currencyId) {
      query = `&currencyId=${currencyId}`;
    } else if (baseCurrency?.id) {
      query = `&currencyId=${baseCurrency?.id}`;
    }

    if (accountId) {
      if (type === AccountType.DEBIT) {
        getDebitAccountBalanceById({
          id: accountId,
          query,
          typeAcc: type,
        });
      } else if (type === AccountType.CREDIT) {
        getCreditAccountBalanceById({
          id: accountId,
          query,
          typeAcc: type,
        });
      }
    }
  };

  const handleClose = useCallback(() => {
    dispatch(clearGeneralLedgerTransactionState());

    setTimeout(() => {
      if (restatement) {
        navigate(`${ROUTES.RESTATEMENT_TRANSACTIONS}${search || ''}`);
      } else {
        navigate(`${ROUTES.GENERAL_LEDGER}${search || ''}`);
      }
    }, 100);
  }, [search]);

  useEffect(() => {
    if (!panelIsOpen && prevPanelIsOpen) {
      dispatch(clearGeneralLedgerTransactionState());
      onClearTimer(true);
    }
  }, [panelIsOpen, prevPanelIsOpen]);

  useEffect(() => {
    if (transactionId && panelIsOpen) {
      getTransaction();
    }
  }, [dispatch, panelIsOpen, transactionId]);

  useEffect(() => {
    if (deleteIsSuccess || createIsSuccess) {
      handleClose();

      if (deleteIsSuccess) {
        getTransactionsList(
          `?take=${selectedTake || '25'}${search?.replace('?', '&')}`,
        );
      }
    }
  }, [deleteIsSuccess, createIsSuccess]);

  useEffect(() => {
    if (debitAccountNumber && debitAccounts) {
      const debitAccountId = debitAccounts?.find(
        (item) => item.fullNumber === debitAccountNumber,
      )?.id;
      setValue('debitAccountId', debitAccountId);

      if (debitAccountId) {
        getAllDebitAccountBalancesAction(debitAccountId);
      }
    }
  }, [debitAccountNumber, debitAccounts]);

  useEffect(() => {
    if (creditAccountNumber && creditAccounts) {
      const creditAccountId = creditAccounts?.find(
        (item) => item.fullNumber === creditAccountNumber,
      )?.id;
      setValue('creditAccountId', creditAccountId);

      if (creditAccountId) {
        getAllCreditAccountBalancesAction(creditAccountId);
      }
    }
  }, [creditAccountNumber, creditAccounts]);

  useEffect(() => {
    if (debitBalances && creditBalances && currencies && baseCurrency) {
      const currenciesList: SelectItemProps[] = [];

      if (
        debitBalances.find((item) => item.currencyId === baseCurrency.id) &&
        creditBalances?.find((item) => item.currencyId === baseCurrency.id)
      ) {
        currenciesList.push({
          name: baseCurrency.name,
          value: baseCurrency.id,
          suffix: baseCurrency?.description,
        });
      }

      currencies
        ?.filter((item) => item.value !== baseCurrency?.id)
        .forEach((item) => {
          if (
            debitBalances?.find(
              (balance) => balance.currencyId === item.value,
            ) &&
            creditBalances?.find((balance) => balance.currencyId === item.value)
          ) {
            currenciesList.push(item);
          }
        });

      setAvailableCurrencies(currenciesList);
    }
  }, [debitBalances, creditBalances, currencies, baseCurrency]);

  useEffect(() => {
    if (transactionId && transaction) {
      getDebitAccountBalanceById({
        id: transaction?.debitAccountId as string,
        query: `&currencyId=${transaction?.currencyId}`,
        typeAcc: AccountType.DEBIT,
      });
      getCreditAccountBalanceById({
        id: transaction?.creditAccountId as string,
        query: `&currencyId=${transaction?.currencyId}`,
        typeAcc: AccountType.CREDIT,
      });
    }
  }, [transactionId, transaction]);

  useEffect(() => {
    if (transactionId && isDirty) {
      onClearTimer();
    }
  }, [isDirty, transactionId]);

  useEffect(() => {
    if (currencyId) {
      if (fields.debitAccountId) {
        onUpdateBalance(AccountType.DEBIT, fields.debitAccountId);
      }
      if (fields.creditAccountId) {
        onUpdateBalance(AccountType.CREDIT, fields.creditAccountId);
      }
    }
  }, [currencyId, fields.creditAccountId, fields.debitAccountId]);

  let onDelete;

  if (
    activePermissions?.includes(
      PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_DELETE_ACTION,
    )
  ) {
    onDelete = transaction?.parentTransactionId
      ? undefined
      : () => setDeletePopupVisible(true);
  } else {
    onDelete = undefined;
  }

  return {
    deletePopupVisible,
    setDeletePopupVisible,
    errorMessage,
    setErrorMessage,
    deleteError,
    setDeleteError,
    availableCurrencies,
    setAvailableCurrencies,
    attachedDocumentsErrors,
    uploadedFilesError,
    uploadedDocumentsErrors,
    imgLink,
    documentsIsLoading,
    documentsIsFetching,
    create,
    createIsLoading,
    debitBalanceIsLoading,
    debitBalanceIsFetching,
    creditBalanceIsLoading,
    creditBalanceIsFetching,
    changeLogsIsLoading,
    changeLogsIsFetching,
    checkDocumentNameInProgress,
    fileUploadInProgress,
    downloadInProgress,
    downloadIsFetching,
    downloadFileIsSuccess,
    commentInProgress,
    accountsCategoriesLoading,
    documentIsLoading,
    documentIsFetching,
    debitAccountsListLoading,
    debitAccountsListUpdateInProgress,
    allDebitAccountBalancesIsLoading,
    allDebitAccountBalancesisFetching,
    allCreditAccountBalancesIsLoading,
    allCreditAccountBalancesisFetching,
    addDocumentInProgress,
    fileUploadIsSuccess,
    documentStatusChangeInProgress,
    documentCategoryChangeInProgress,
    editCommentInProgress,
    editCommentIsSuccess,
    deleteCommentInProgress,
    deleteDocumentInProgress,
    deleteDocumentIsSuccess,
    creditAccountsListLoading,
    creditAccountsListUpdateInProgress,
    deleteIsSuccess,
    deleteIsLoading,
    isLoading,
    internalError,
    getTransationDocumentsAction,
    linkedTransactionPath,
    onDeleteDocument,
    onMoveDocument,
    onChangeCustomerDocumentsStatus,
    onDownloadDocument,
    getTransactionChangeLogsAction,
    getDebitAccountsListAction,
    getCreditAccountsListAction,
    onSendDocumentComment,
    onEditComment,
    onDeleteComment,
    onUploadDocument,
    getTransactionDocumentByIdAction,
    handleDelete,
    onSearch,
    onDelete,
    handleClose,
    debitAccounts,
    creditAccounts,
    onUpdateBalance,
    setDebitAccounts,
    setCreditAccounts,
    dateWithTime,
    setDateWithTime,
  };
};

export default useRequests;
